import { useEffect } from 'react';

type Schema = Record<string, unknown>;

type SEOProps = {
  title: string;
  description: string;
  canonical: string;
  ogUrl: string;
  ogImage?: string;
  schema: Schema;
};

const DEFAULT_OG_IMAGE = 'https://lsabill.net/logo192.png';
const SCHEMA_ID = 'page-schema';

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function setSchema(schema: Schema) {
  let element = document.getElementById(SCHEMA_ID) as HTMLScriptElement | null;

  if (!element) {
    element = document.createElement('script');
    element.id = SCHEMA_ID;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.text = JSON.stringify(schema);
}

export default function SEO({
  title,
  description,
  canonical,
  ogUrl,
  ogImage = DEFAULT_OG_IMAGE,
  schema,
}: SEOProps) {
  useEffect(() => {
    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', ogUrl);
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setCanonical(canonical);
    setSchema(schema);
  }, [canonical, description, ogImage, ogUrl, schema, title]);

  return null;
}
